/**
 * CSV Export Utility Functions
 * Provides standardized CSV export functionality for data tables
 */

export interface CSVExportOptions {
  filename?: string;
  includeHeaders?: boolean;
  dateFormat?: string;
  numberFormat?: string;
}

/**
 * Converts an array of objects to CSV format
 */
export function objectsToCSV<T extends Record<string, unknown>>(
  data: T[],
  options: CSVExportOptions = {}
): string {
  if (!data || data.length === 0) {
    return "";
  }

  const { includeHeaders = true } = options;

  // Get headers from the first object
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvRows: string[] = [];

  // Add headers if requested
  if (includeHeaders) {
    csvRows.push(headers.map((header) => `"${header}"`).join(","));
  }

  // Add data rows
  for (const row of data) {
    const values = headers.map((header) => {
      const value = row[header];
      if (value === null || value === undefined) {
        // eslint-disable-next-line quotes
        return '""';
      }
      // Escape quotes and wrap in quotes
      // eslint-disable-next-line quotes
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    });
    csvRows.push(values.join(","));
  }

  return csvRows.join("\n");
}

/**
 * Downloads CSV data as a file
 */
export function downloadCSV(csvContent: string, filename: string = "export.csv"): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Formats a date for CSV export
 */
export function formatDateForCSV(date: string | Date | null | undefined): string {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toISOString().split("T")[0]; // YYYY-MM-DD format
}

/**
 * Formats a number for CSV export
 */
export function formatNumberForCSV(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return value.toFixed(2);
}

/**
 * Formats currency for CSV export.
 *
 * Re-exported from the canonical `@wtc/ui` implementation, which is identical
 * (`$` prefix + `toFixed(2)`, empty string when absent). Keeps CSV output byte
 * for byte unchanged.
 */
export { formatCurrencyForCSV } from "@wtc/ui";

/**
 * Formats boolean for CSV export
 */
export function formatBooleanForCSV(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return "";
  return value ? "Yes" : "No";
}

/**
 * Formats array for CSV export
 */
export function formatArrayForCSV(value: unknown[] | null | undefined): string {
  if (!value || !Array.isArray(value)) return "";
  return value.join(", ");
}

/**
 * Sanitizes field names for CSV headers
 */
export function sanitizeFieldName(fieldName: string): string {
  return fieldName
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase())
    .trim();
}
